"use client";

import * as React from "react";
import { parseContainerNumbers } from "@repo/contracts/logistics-workflow";
import { X } from "lucide-react";
import { Textarea } from "../primitives/textarea";
import { RecordDrawer } from "./record-drawer";

export type ContainerNumberEditorLabels = {
  placeholder: string;
  validCount: (count: number) => string;
  invalid: string;
  tooMany: string;
  remove: (number: string) => string;
};

export function ContainerNumberEditor({
  value,
  onChange,
  labels,
}: {
  value: string;
  onChange: (value: string) => void;
  labels: ContainerNumberEditorLabels;
}) {
  const parsed = parseContainerNumbers(value);
  const invalid = parsed.invalid.length > 0 || parsed.tooMany;

  const remove = (number: string) => {
    const next = [
      ...parsed.valid.filter((item) => item !== number),
      ...parsed.invalid,
    ];
    onChange(next.join("\n"));
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={value}
        aria-invalid={invalid}
        placeholder={labels.placeholder}
        className="min-h-36 w-full font-mono"
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        {labels.validCount(parsed.valid.length)}
      </p>
      {parsed.tooMany ? (
        <p className="text-sm text-destructive">{labels.tooMany}</p>
      ) : null}
      {parsed.invalid.length > 0 ? (
        <div className="space-y-1">
          <p className="text-sm text-destructive">{labels.invalid}</p>
          <ul className="space-y-1">
            {parsed.invalid.map((token) => (
              <li key={token} className="font-mono text-sm text-destructive">
                {token}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {parsed.valid.length > 0 ? (
        <ul className="grid max-h-60 grid-cols-1 gap-x-3 gap-y-1 overflow-y-auto sm:grid-cols-2 lg:grid-cols-4">
          {parsed.valid.map((number, index) => (
            <li
              key={number}
              className="flex min-w-0 items-center gap-1 font-mono text-sm"
            >
              <span className="shrink-0 text-muted-foreground">
                {String(index + 1).padStart(parsed.valid.length > 99 ? 3 : 2, "0")}.
              </span>
              <span className="min-w-0 flex-1 truncate">{number}</span>
              <button
                type="button"
                className="shrink-0 rounded-sm text-muted-foreground hover:text-destructive focus-visible:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={labels.remove(number)}
                onClick={() => remove(number)}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export type ContainerNumberDrawerLabels = {
  filterPlaceholder: string;
  noMatch: string;
};

export function ContainerNumberDrawer({
  open,
  onOpenChange,
  title,
  numbers,
  labels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  numbers: string[];
  labels: ContainerNumberDrawerLabels;
}) {
  const [filter, setFilter] = React.useState("");
  const needle = filter.replace(/\s+/g, "").toUpperCase();
  const visible = needle
    ? numbers.filter((number) => number.includes(needle))
    : numbers;

  return (
    <RecordDrawer
      open={open}
      title={title}
      onOpenChange={(next) => {
        if (!next) setFilter("");
        onOpenChange(next);
      }}
    >
      <label className="block space-y-2">
        <span className="sr-only">{labels.filterPlaceholder}</span>
        <input
          value={filter}
          placeholder={labels.filterPlaceholder}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          onChange={(event) => setFilter(event.target.value)}
        />
      </label>
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">{labels.noMatch}</p>
      ) : (
        <ul className="grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-3">
          {visible.map((number) => (
            <li key={number} className="px-2 py-1 font-mono text-sm">
              {number}
            </li>
          ))}
        </ul>
      )}
    </RecordDrawer>
  );
}
